import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { tokenStorage, UserProfile, mockMode, isSessionExpiryError } from './auth0-config';
import { apiSdk } from '@/api/sdk';
import { Button } from '@/components/ui/button';

/**
 * Auth0 Provider Wrapper for Cloud Landing Platform
 *
 * This component provides Auth0 authentication context to the React application.
 * Supports both real Auth0 authentication and mock mode for development.
 */

// Extended authentication context interface
interface AuthContextType {
  // Auth state
  isLoading: boolean;
  isAuthenticated: boolean;
  user: UserProfile | null;
  isMockMode: boolean;
  isSdkReady: boolean; // True when API SDK has been initialized

  // Auth methods
  login: (returnTo?: string) => Promise<void>;
  logout: (returnToUrl?: string) => Promise<void>;
  getAccessTokenSilently: () => Promise<string>;

  // Mock mode methods
  switchMockUser?: (userKey: string) => Promise<void>;
}

// Create the authentication context
const AuthContext = createContext<AuthContextType | null>(null);

// Auth0 Provider component props
interface Auth0ProviderProps {
  children: ReactNode;
}

// Mock Auth Provider for development without Auth0
const MockAuthProvider: React.FC<Auth0ProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isSdkReady, setIsSdkReady] = useState(false);

  useEffect(() => {
    // Check if we have a stored user from a previous mock login
    const storedUser = tokenStorage.getUser();
    const storedAuth = localStorage.getItem('auth0_is_authenticated');

    if (storedUser && storedAuth === 'true') {
      setUser(storedUser);
      setIsAuthenticated(true);
      // Initialize API SDK in mock mode
      apiSdk.initializeMockMode();
      setIsSdkReady(true);
    }
    setIsLoading(false);
  }, []);

  const login = async () => {
    setIsLoading(true);
    try {
      const mockUser = await mockMode.login('owner');
      setUser(mockUser);
      setIsAuthenticated(true);
      // Initialize API SDK in mock mode
      apiSdk.initializeMockMode();
      setIsSdkReady(true);
    } catch (error) {
      console.error('Mock login failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    mockMode.logout();
    apiSdk.reset();
    setIsSdkReady(false);
    setUser(null);
    setIsAuthenticated(false);
  };

  const getAccessTokenSilently = async (): Promise<string> => {
    const token = tokenStorage.getAccessToken();
    if (!token) {
      throw new Error('No access token available');
    }
    return token;
  };

  const switchMockUser = async (userKey: string) => {
    setIsLoading(true);
    try {
      const newUser = await mockMode.switchUser(userKey);
      setUser(newUser);
    } catch (error) {
      console.error('Failed to switch mock user:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Memoized so consumers (App, route guards) only re-render on real auth
  // state changes — a fresh object every render fans out re-renders app-wide.
  const contextValue = useMemo<AuthContextType>(() => ({
    isLoading,
    isAuthenticated,
    user,
    isMockMode: true,
    isSdkReady,
    login,
    logout,
    getAccessTokenSilently,
    switchMockUser,
  }), [isLoading, isAuthenticated, user, isSdkReady]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// auth0-spa-js resolves `undefined` (instead of a token) once the session
// lifetime ceiling is reached. That session is over, so surface it as the same
// `login_required` error the session-expiry handling already recognises.
export async function requireToken(token: Promise<string | undefined>): Promise<string> {
  const value = await token;
  if (!value) {
    throw Object.assign(new Error('login_required: session has ended'), { error: 'login_required' });
  }
  return value;
}

// Real Auth0 provider wrapper
const RealAuth0Provider: React.FC<Auth0ProviderProps> = ({ children }) => {
  const {
    isLoading: auth0Loading,
    isAuthenticated: auth0Authenticated,
    user: auth0User,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently: auth0GetToken
  } = useAuth0();

  const [user, setUser] = useState<UserProfile | null>(null);
  const [isSdkReady, setIsSdkReady] = useState(false);

  useEffect(() => {
    if (auth0Authenticated && auth0User) {
      // Transform Auth0 user to our UserProfile format
      const userProfile: UserProfile = {
        sub: auth0User.sub!,
        email: auth0User.email,
        email_verified: auth0User.email_verified,
        name: auth0User.name,
        nickname: auth0User.nickname,
        picture: auth0User.picture,
        updated_at: auth0User.updated_at,
        // Extract custom claims using audience namespace
        roles: (auth0User as any)[`${import.meta.env.VITE_AUTH0_API_AUDIENCE}/roles`] || [],
        permissions: (auth0User as any)[`${import.meta.env.VITE_AUTH0_API_AUDIENCE}/permissions`] || [],
        user_metadata: (auth0User as any)[`${import.meta.env.VITE_AUTH0_API_AUDIENCE}/user_metadata`] || {},
        app_metadata: (auth0User as any)[`${import.meta.env.VITE_AUTH0_API_AUDIENCE}/app_metadata`] || {}
      };

      setUser(userProfile);

      // Initialize API SDK with Auth0 methods
      apiSdk.initialize({
        getAccessTokenSilently: (options) => requireToken(auth0GetToken(options)),
        logout: async () => {
          tokenStorage.clear();
          await auth0Logout({
            logoutParams: { returnTo: window.location.origin }
          });
        }
      });

      // Mark SDK as ready AFTER initialization
      setIsSdkReady(true);

      // Warm the Auth0 SDK token cache immediately after authentication. The
      // SDK manages its own (localStorage) cache — we keep no separate copy.
      auth0GetToken().catch(error => {
        console.error('Failed to get access token:', error);
      });
    } else if (!auth0Authenticated) {
      setUser(null);
      tokenStorage.removeUser();
      setIsSdkReady(false);
      apiSdk.reset();
    }
  }, [auth0Authenticated, auth0User, auth0GetToken, auth0Logout]);

  // Proactively refresh token when user returns to tab (only if needed)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible' || !auth0Authenticated) {
        return;
      }

      try {
        // Use cached token first - Auth0 SDK handles expiry internally
        await auth0GetToken();
      } catch (error: any) {
        // Only redirect on actual session expiry, not transient errors
        if (isSessionExpiryError(error)) {
          console.warn('Session expired on visibility change:', error?.message);
          tokenStorage.clear();
          await loginWithRedirect({
            appState: { returnTo: window.location.pathname + window.location.search }
          });
        } else {
          // Log but don't redirect - could be transient network issue
          console.warn('Token refresh failed (non-fatal):', error?.message);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [auth0Authenticated, auth0GetToken, loginWithRedirect]);

  // Proactive token refresh interval to keep session alive
  useEffect(() => {
    if (!auth0Authenticated) return;

    const refreshInterval = setInterval(async () => {
      try {
        await auth0GetToken();
      } catch (error: any) {
        // Only redirect on actual session expiry, not transient errors
        if (isSessionExpiryError(error)) {
          console.warn('Session expired during proactive refresh:', error?.message);
          tokenStorage.clear();
          await loginWithRedirect({
            appState: { returnTo: window.location.pathname + window.location.search }
          });
        }
      }
    }, 10 * 60 * 1000); // Every 10 minutes

    return () => clearInterval(refreshInterval);
  }, [auth0Authenticated, auth0GetToken, loginWithRedirect]);

  // `returnTo` is the in-app path to restore after authentication; it rides
  // through Auth0 via appState and is applied by onRedirectCallback (main.tsx).
  const login = async (returnTo?: string) => {
    await loginWithRedirect({
      appState: { returnTo: returnTo || window.location.pathname + window.location.search }
    });
  };

  const logout = async (returnToUrl?: string) => {
    tokenStorage.clear();
    const logoutUri = returnToUrl || import.meta.env.VITE_AUTH0_LOGOUT_URI || window.location.origin;
    await auth0Logout({
      logoutParams: {
        returnTo: logoutUri
      }
    });
  };

  const getAccessTokenSilently = async (): Promise<string> => {
    return requireToken(auth0GetToken());
  };

  // Memoized so consumers (App, route guards) only re-render on real auth
  // state changes — a fresh object every render fans out re-renders app-wide
  // (and remounted the whole protected subtree before the route guards were
  // hoisted to module scope).
  const contextValue = useMemo<AuthContextType>(() => ({
    isLoading: auth0Loading,
    isAuthenticated: auth0Authenticated,
    user,
    isMockMode: false,
    isSdkReady,
    login,
    logout,
    getAccessTokenSilently,
    // login/logout/getAccessTokenSilently close over the stable auth0-react
    // callbacks; the state they read is covered by the deps below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [auth0Loading, auth0Authenticated, user, isSdkReady]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Main Auth0 Provider - chooses between mock and real based on configuration
export const Auth0Provider: React.FC<Auth0ProviderProps> = ({ children }) => {
  // Check if we're using mock mode
  if (mockMode.isEnabled()) {
    return <MockAuthProvider>{children}</MockAuthProvider>;
  }

  return <RealAuth0Provider>{children}</RealAuth0Provider>;
};

// Hook to use the authentication context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an Auth0Provider');
  }
  return context;
};

// Mock user switcher component for development
export const MockUserSwitcher: React.FC = () => {
  const { isMockMode, switchMockUser, user, isLoading } = useAuth();

  if (!isMockMode || !switchMockUser) {
    return null;
  }

  const mockUsers = mockMode.getMockUsers();

  return (
    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-2xl">
      <h3 className="text-sm font-medium text-yellow-800 mb-2">Mock Mode - Switch User</h3>
      <div className="flex flex-wrap gap-2">
        {mockUsers.map((mockUser) => (
          <Button
            key={mockUser.key}
            size="dense"
            variant={user?.email === mockUser.email ? 'primary' : 'secondary'}
            onClick={() => switchMockUser(mockUser.key)}
            disabled={isLoading}
          >
            {mockUser.name}
          </Button>
        ))}
      </div>
      {user && (
        <p className="text-xs text-yellow-600 mt-2">
          Current: {user.name} ({user.roles?.join(', ') || 'No roles'})
        </p>
      )}
    </div>
  );
};

export default Auth0Provider;
